import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { useAuth } from '@/lib/auth';
import { NotificationsService, type Notification } from '@/data/notifications.service';
import { logger } from '@/lib/logger';

/**
 * Hook to get notifications for the current user
 */
export function useNotifications(filters?: {
  isRead?: boolean;
  scheduledForFrom?: string;
  scheduledForTo?: string;
  type?: string;
}) {
  const { scope, isLoading: scopeLoading } = useWorkspaceScope();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['notifications', scope, user?.id, filters],
    queryFn: async (): Promise<Notification[]> => {
      if (!user?.id) return [];
      if (scope.type === 'all' && scope.workspaceIds.length === 0) return [];
      if (scope.type === 'single' && !scope.workspaceId) return [];

      return NotificationsService.getNotifications(scope, user.id, filters);
    },
    enabled: !scopeLoading && !!user?.id && (scope.type === 'all' ? scope.workspaceIds.length > 0 : !!scope.workspaceId),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to create a notification
 */
export function useCreateNotification() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceScope();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: Omit<Notification, 'id' | 'created_at' | 'read_at'>) => {
      if (!activeWorkspaceId) throw new Error('No workspace available');
      if (!user?.id) throw new Error('No user available');

      return NotificationsService.createNotification({
        ...input,
        workspace_id: activeWorkspaceId,
        user_id: user.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error) => {
      logger.error('[useCreateNotification] Failed to create notification', error);
    },
  });
}

/**
 * Hook to mark notification as read
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceScope();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      if (!activeWorkspaceId) throw new Error('No workspace available');

      return NotificationsService.markAsRead({
        id: notificationId,
        workspace_id: activeWorkspaceId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error) => {
      logger.error('[useMarkNotificationRead] Failed to mark notification as read', error);
    },
  });
}

/**
 * Hook to delete notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceScope();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      if (!activeWorkspaceId) throw new Error('No workspace available');

      return NotificationsService.deleteNotification(notificationId, activeWorkspaceId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error) => {
      logger.error('[useDeleteNotification] Failed to delete notification', error);
    },
  });
}
